import React from 'react'
import { Link, useRouteMatch } from 'react-router-dom'

import { Container } from '../ui'
import { image } from '../../lib/image'
import { cutoffLabel } from '../../lib/same-day'
import { chatEntryProps, whatsappChatProps } from '../whatsapp-chat'
import { PHONE_NUMBER } from '../../store/constants/contacts'
import { readWashesMenu } from '../header/washes-menu'

import './style'

// THE FOOTER. DESIGN.md §6 ("page chrome is not a home-page section") and §7.8.
//
// WHAT A FOOTER IS FOR ON THIS SITE, now that two things it used to carry are
// built above it:
//
//   - `pages/home/sections/areas-cta` ends the page. It is the one centred
//     block in the design, it states the promise again and it holds the ink
//     button. The page already has its goodbye, so the footer is NOT a second
//     one. There is no heading here, no pitch, no button and nothing to sign
//     up to: a visitor who has reached this far has either booked or is
//     looking something up.
//   - That same slot renders all 46 area pages, grouped by geography, in the
//     block directly above this one. So the footer does not list areas and
//     does not describe where we wash. The old "Locations" paragraph said
//     less than the list two hundred pixels above it.
//
// What is left is the colophon, and it is the only thing here:
//
//   1. WHO THIS IS. The wordmark, the registered name and the locality. The
//      locality is the one address a service-area business may publish and it
//      matches SeoHelper::ADDRESS exactly, so the page and the structured
//      data agree.
//   2. HOW TO REACH A HUMAN. WhatsApp, the website chat, email, the Google
//      Business Profile, and the hours those routes are answered. This is
//      the name, address and number that local search expects to find in the
//      page rather than only in the JSON-LD, and after the server block is
//      folded into the app (see the note on the duplicate list below) this
//      is the only place on the site that prints it.
//   3. WHERE THE DOCUMENTS ARE. Terms, privacy, the cookie statement, and the
//      pages that are nobody's first destination: FAQs, corporate, operators.
//
// NOT A NAVY SLAB. It was `background: #0a2540` with white text, which is
// where the owner's "feels out of place now" came from: a reversed block is a
// full stop, and the page already has one. There is no navy in the palette
// (§1 lists ten colours and #0a2540 is not among them), the one dark shape on
// the page is meant to be the CTA button a few hundred pixels up, and a
// full-bleed `--ink` band under it would swallow it. So the footer takes
// `--stone`, the alternate band of §6, and the page ends on the rhythm it
// kept the whole way down: paper CTA, stone footer. On stone `--ink` is
// 14.5:1, `--mute` 5.6:1 and `--accent` 4.9:1, all past the 4.5:1 floor (§7).
//
// THE WORDMARK is `assets/images/wordmark.svg`, resolved through
// `lib/image.js`. Replacing the mark is replacing that one file. Going light
// also means the footer uses the SAME mark as the header rather than
// `wordmark-white.svg`, so the replacement only has to be drawn once; nothing
// in the app imports the white file any more.

// SeoHelper::EMAIL. The one address support is read on.
const EMAIL = 'support@gowashmycar.com'

// NO PHONE CALL. Robin, 26 Oct 2026: "dont think we should offer to call the
// phone number on the website" and "whatsapp and chat and email enough imo".
// So the contact routes are WhatsApp, the website chat and email, and the
// number is printed once, as the WhatsApp link's label: it is the WhatsApp
// business number and the same one SeoHelper::PHONE gives the JSON-LD and the
// Business Profile, so the page and the listing still agree. There was a tel:
// link here until then. No digit of the number is typed in this file:
// `PHONE_NUMBER` is the spaced form from store/constants/contacts.js, and the
// link is whatsappChatProps (wa.me). docs/DECISIONS.md, 26 Oct 2026.

// The Google Business Profile, read from the `gwmc:reviews` meta tag the
// layout writes on every page (MetaTags::Helper#google_reviews carries
// SeoHelper::GBP_URL in it). Same mechanism the reviews section uses, and for
// the same reason: the place id lives in one Ruby constant and nothing in the
// bundle holds a copy of it. No tag, no link, rather than a guessed one.
const businessProfileUrl = () => {
  if (typeof document === 'undefined') return null

  const tag = document.querySelector("meta[name='gwmc:reviews']")?.getAttribute('content')
  if (!tag) return null

  try {
    return JSON.parse(tag).url || null
  } catch (e) {
    return null
  }
}

// The two social glyphs, drawn here rather than imported. `footer_instagram.svg`
// and `footer_facebook.svg` are filled white for the navy ground that is gone,
// and an icon package for two shapes is not a trade the bundle should make
// (§3: no new dependency). Stroked outlines in `currentColor` so the colour is
// the token the link already carries, the same way the areas chevron is drawn
// in CSS. Decorative: the accessible name is on the anchor.
const InstagramGlyph = () => (
  <svg
    className="footer-social-glyph"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
    focusable="false">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4.2" />
    <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
  </svg>
)

const FacebookGlyph = () => (
  <svg
    className="footer-social-glyph"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false">
    <path d="M15.5 3h-2.2a4.3 4.3 0 0 0-4.3 4.3V10H6.5v3.2H9V21h3.3v-7.8h2.6V10h-2.6V7.6c0-.7.6-1.3 1.3-1.3h1.9Z" />
  </svg>
)

// One row of a link column. Every one of these is 44px tall on a phone (§3:
// nothing interactive is smaller), which is the floor a column of small legal
// links usually breaks. `.gwmc-link` carries the colour and the hover
// underline; `.footer-link` the box and the type, and both classes are named
// because `.gwmc-link` declares `font-weight: inherit` and primitives.css is
// emitted after this component's sheet.
const FooterLink = ({ to, href, children, ...rest }) => {
  if (to) {
    return (
      <li className="footer-item">
        <Link className="gwmc-link footer-link" to={to} {...rest}>
          {children}
        </Link>
      </li>
    )
  }

  return (
    <li className="footer-item">
      <a className="gwmc-link footer-link" href={href} {...rest}>
        {children}
      </a>
    </li>
  )
}

export const Footer = React.memo(() => {
  const isBookingFinishPage = !!useRouteMatch('/booking/finish')
  const isBookingPage = !!useRouteMatch('/booking/:step') && !isBookingFinishPage

  if (isBookingPage) return null

  const profileUrl = businessProfileUrl()
  // Every wash page and add-on page, from the same gwmc:catalogue tag as the
  // header's Washes menu (6 Oct 2026): names only, plain document links,
  // because the pages are server-rendered. Internal links for search too.
  const washesMenu = readWashesMenu()
  const washLinks = [...washesMenu.washes, ...washesMenu.pages]

  return (
    <footer className="footer">
      <Container className="footer-inner">
        <div className="footer-grid">
          <div className="footer-identity">
            <Link className="footer-wordmark" to="/">
              <img width="137" height="24" src={image('./wordmark.svg')} alt="GoWashMyCar" />
            </Link>
            <p className="footer-tagline">
              Mobile car washing and valeting in London since 2016. Professional valeters, at your door.
            </p>
            <p className="footer-address">GoWashMyCar Ltd, London, Greater London</p>
            <p className="footer-hours">
              Washes run every day, 8am to 6pm. Book today’s wash until {cutoffLabel()}.
            </p>
          </div>

          <div className="footer-group footer-group--contact">
            <h2 className="footer-group-title" id="footer-contact">
              Contact
            </h2>
            <ul className="footer-list" aria-labelledby="footer-contact">
              <li className="footer-item">
                <a className="gwmc-link footer-link" {...whatsappChatProps('Hi GoWashMyCar!')}>
                  WhatsApp {PHONE_NUMBER}
                </a>
              </li>
              <li className="footer-item">
                <a className="gwmc-link footer-link" {...chatEntryProps('Hi GoWashMyCar!')}>
                  Website chat
                </a>
              </li>
              <FooterLink href={`mailto:${EMAIL}`}>{EMAIL}</FooterLink>
              {profileUrl && (
                <FooterLink href={profileUrl} target="_blank" rel="noopener noreferrer">
                  Google Business Profile
                  <span className="gwmc-sr-only"> (opens in a new tab)</span>
                </FooterLink>
              )}
            </ul>
          </div>

          {washLinks.length > 0 && (
            <nav className="footer-group footer-group--washes" aria-labelledby="footer-washes">
              <h2 className="footer-group-title" id="footer-washes">
                Washes
              </h2>
              <ul className="footer-list">
                {washLinks.map(wash => (
                  <FooterLink key={wash.path} href={wash.path}>
                    {wash.name}
                  </FooterLink>
                ))}
              </ul>
            </nav>
          )}

          <nav className="footer-group" aria-labelledby="footer-company">
            <h2 className="footer-group-title" id="footer-company">
              Company
            </h2>
            <ul className="footer-list">
              <FooterLink to="/services">Prices</FooterLink>
              <FooterLink to="/faqs">FAQs</FooterLink>
              <FooterLink to="/corporate">Corporate</FooterLink>
              <FooterLink to="/operators">Become an operator</FooterLink>
            </ul>
          </nav>

          <nav className="footer-group" aria-labelledby="footer-legal">
            <h2 className="footer-group-title" id="footer-legal">
              Legal
            </h2>
            <ul className="footer-list">
              <FooterLink to="/terms_and_conditions">Terms &amp; conditions</FooterLink>
              <FooterLink to="/privacy_policy">Privacy policy</FooterLink>
            </ul>
          </nav>
        </div>

        <div className="footer-end">
          {/* The cookie statement. It was 180 words of footer and it is the
              only cookie disclosure the site has: the privacy policy does not
              mention them and the terms point at a "Cookies Policy" that does
              not exist. So it is kept, and kept short, until there is a page
              to link to. */}
          <p className="footer-cookies">
            This site uses cookies to keep your booking working and to measure how the site is used.
            If you carry on browsing, we take that as acceptance.
          </p>

          <div className="footer-end-row">
            <p className="footer-copyright">© GoWashMyCar Ltd. {new Date().getFullYear()}</p>
            <ul className="footer-social">
              <li>
                <a
                  className="footer-social-link"
                  href="https://www.instagram.com/gowashmycar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GoWashMyCar on Instagram">
                  <InstagramGlyph />
                </a>
              </li>
              <li>
                <a
                  className="footer-social-link"
                  href="https://www.facebook.com/gowashmycar"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GoWashMyCar on Facebook">
                  <FacebookGlyph />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Container>
    </footer>
  )
})
